import {
  DROPDOWN_ORGAN_TYPE,
  MARCO_LEGAL_OPTIONS,
  OBJETIVOS_OPTIONS,
  VERTICAIS_OPTIONS
} from 'domain/feature/hotsite/Forms/NewPublicOrganRegister/constants'
import type { IPublicAdministrationEcosystemRow, IPublicAdministrationInfo } from './contracts'

const ORGAN_TYPE_OPTIONS = DROPDOWN_ORGAN_TYPE.filter(o => o.value !== '')

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

export const normalizePublicAdministrationListResponse = (
  data: unknown
): { rows: IPublicAdministrationEcosystemRow[]; totalRows: number } => {
  if (data == null || typeof data !== 'object') {
    return { rows: [], totalRows: 0 }
  }
  const o = data as Record<string, unknown>
  const rawRows = o.rows ?? o.Rows
  const rows = (Array.isArray(rawRows) ? rawRows : []) as IPublicAdministrationEcosystemRow[]
  const totalRowsRaw = o.totalRows ?? o.TotalRows
  let totalRows =
    typeof totalRowsRaw === 'number' && !Number.isNaN(totalRowsRaw) ? totalRowsRaw : rows.length
  if (totalRows === 0 && rows.length > 0) {
    totalRows = rows.length
  }
  return { rows, totalRows }
}

const isEmptyGuid = (id: string | undefined | null): boolean => {
  if (id == null) return true
  const s = String(id).trim().toLowerCase()
  return s === '' || s === EMPTY_GUID
}

export const resolvePublicAdministrationEntityId = (pa: IPublicAdministrationInfo | null | undefined): string => {
  if (pa == null) return 'unknown'
  const rawId = pa.id != null ? String(pa.id).trim() : ''
  const companyId = pa.companyId != null ? String(pa.companyId).trim() : ''
  if (!isEmptyGuid(rawId)) return rawId
  if (companyId !== '') return companyId
  return 'unknown'
}

const trimNonEmpty = (v: unknown): string | undefined => {
  if (v == null) return undefined
  const s = String(v).trim()
  if (s === '' || s === '-') return undefined
  return s
}

const onlyDigits = (value: unknown): string => String(value ?? '').replace(/\D/g, '')

const formatCnpj = (value: unknown): string | undefined => {
  const digits = onlyDigits(value)
  if (digits.length !== 14) return trimNonEmpty(value)
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

const formatCpf = (value: unknown): string | undefined => {
  const digits = onlyDigits(value)
  if (digits.length !== 11) return trimNonEmpty(value)
  return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
}

const formatPhoneBr = (value: unknown): string | undefined => {
  const digits = onlyDigits(value)
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
  }
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3')
  }
  return trimNonEmpty(value)
}

const parseMoneyValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const raw = value.trim()
  if (raw === '') return null
  const cleaned = raw.replace(/\s/g, '').replace(/R\$/gi, '')
  let normalized = cleaned
  if (cleaned.includes(',') && cleaned.includes('.')) {
    normalized = cleaned.replace(/\./g, '').replace(',', '.')
  } else if (cleaned.includes(',')) {
    normalized = cleaned.replace(',', '.')
  }
  const parsed = Number(normalized)
  if (Number.isFinite(parsed)) return parsed
  return null
}

const formatCurrencyBr = (value: unknown): string | undefined => {
  const parsed = parseMoneyValue(value)
  if (parsed == null) return trimNonEmpty(value)
  return parsed.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}


export const resolvePublicAdministrationCityState = (
  pa: IPublicAdministrationInfo | null | undefined,
  row?: IPublicAdministrationEcosystemRow
): { city?: string; state?: string } => {
  const cityCandidates: unknown[] = []
  const stateCandidates: unknown[] = []

  const collectFromAddress = (addr: unknown): void => {
    if (addr == null || typeof addr !== 'object') return
    const a = addr as Record<string, unknown>
    cityCandidates.push(a.city, a.City)
    stateCandidates.push(a.state, a.State, a.uf, a.UF)
  }

  if (pa != null) {
    collectFromAddress(pa.address)
    collectFromAddress((pa as Record<string, unknown>).Address)
    const root = pa as Record<string, unknown>
    cityCandidates.push(root.city, root.City)
    stateCandidates.push(root.state, root.State, root.uf, root.UF)
  }

  if (row != null) {
    const r = row as Record<string, unknown>
    collectFromAddress(r.address)
    collectFromAddress(r.Address)
    cityCandidates.push(r.city, r.City)
    stateCandidates.push(r.state, r.State, r.uf, r.UF)
  }

  let city: string | undefined
  for (const c of cityCandidates) {
    const t = trimNonEmpty(c)
    if (t !== undefined) {
      city = t
      break
    }
  }
  let state: string | undefined
  for (const s of stateCandidates) {
    const t = trimNonEmpty(s)
    if (t !== undefined) {
      state = t
      break
    }
  }

  return { city, state }
}

const getPublicAdministrationInfoFromRow = (
  row: IPublicAdministrationEcosystemRow
): IPublicAdministrationInfo | null | undefined => {
  const r = row as Record<string, unknown>
  const rowData = (r.data ?? r.Data) as Record<string, unknown> | undefined
  return (
    row.publicAdministrationInfo ??
    (r.PublicAdministrationInfo as IPublicAdministrationInfo | undefined) ??
    (rowData?.publicAdministration as IPublicAdministrationInfo | undefined) ??
    (rowData?.PublicAdministration as IPublicAdministrationInfo | undefined) ??
    null
  )
}

const boolToSimNao = (v: unknown): string | undefined => {
  if (v === true) return 'Sim'
  if (v === false) return 'Não'
  return undefined
}

const formatIsoDateBr = (v: unknown): string | undefined => {
  if (v == null || v === '') return undefined
  if (typeof v !== 'string') return undefined
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

const proceduresIndicesToLabel = (arr: unknown): string | undefined => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined
  const labels = arr
    .filter((i): i is number => typeof i === 'number' && i >= 0 && i < MARCO_LEGAL_OPTIONS.length)
    .map(i => MARCO_LEGAL_OPTIONS[i].label)
  return labels.length > 0 ? labels.join(', ') : undefined
}

const priorityImpactIndicesToLabel = (arr: unknown): string | undefined => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined
  const labels = arr
    .filter((i): i is number => typeof i === 'number' && i >= 0 && i < VERTICAIS_OPTIONS.length)
    .map(i => VERTICAIS_OPTIONS[i].label)
  return labels.length > 0 ? labels.join(', ') : undefined
}

const programGoalIndicesToLabel = (arr: unknown): string | undefined => {
  if (!Array.isArray(arr) || arr.length === 0) return undefined
  const labels = arr
    .filter((i): i is number => typeof i === 'number' && i >= 0 && i < OBJETIVOS_OPTIONS.length)
    .map(i => OBJETIVOS_OPTIONS[i].label)
  return labels.length > 0 ? labels.join(', ') : undefined
}

export const administrationTypeToLabel = (type: number | null | undefined): string => {
  if (type === null || type === undefined || Number.isNaN(Number(type))) return '-'
  const idx = Number(type)
  return ORGAN_TYPE_OPTIONS[idx]?.label ?? String(type)
}

const summarizeInnovationArea = (
  ip: Record<string, unknown> | null | undefined,
  leadership?: Record<string, unknown> | null
): string => {
  if (leadership?.hasInnovationDepartment === true) {
    const dn = leadership.departmentName
    if (typeof dn === 'string') {
      const t = dn.trim()
      if (t !== '' && t !== '-') return t
    }
  }
  if (ip == null) return '-'
  const priority = ip.priorityImpactAreas ?? ip.priorityImpactArea
  const fromPriority = priorityImpactIndicesToLabel(priority)
  if (fromPriority != null) return fromPriority
  const verticals = ip.verticalsOfImpact ?? ip.verticals
  if (Array.isArray(verticals) && verticals.length > 0) {
    return verticals.map(v => String(v)).join(', ')
  }
  const desc = ip.description
  if (typeof desc === 'string' && desc.trim() !== '') return desc.trim()
  return '-'
}

const POPULATION_LABELS: Record<number, string> = {
  0: 'Pequeno porte',
  1: 'Médio porte',
  2: 'Grande porte',
  3: 'Metrópole'
}

export const readEcosystemHiddenFromRow = (
  row: IPublicAdministrationEcosystemRow
): boolean => {
  if (row.ecosystemHidden === true) return true
  const r = row as Record<string, unknown>
  return r.ecosystem_hidden === true || r.EcosystemHidden === true
}

export interface PublicAdministrationListCardModel {
  id: string
  residentCompanyId: string
  residentId: string | null
  ecosystemHidden: boolean
  title: string
  activity: string
  pitch: string
  dirigenteMaximo: string
  numeroServidores: string
  innovationAreaPresence: string
  areaInovacao: string
  state: string
  isInMyEcosystem: boolean
  badgeLabel: string
  logo?: string
  raw: IPublicAdministrationEcosystemRow
}

export const mapPublicAdministrationRowToCard = (
  row: IPublicAdministrationEcosystemRow
): PublicAdministrationListCardModel => {
  const pa = getPublicAdministrationInfoFromRow(row)
  const id = resolvePublicAdministrationEntityId(pa ?? undefined)
  const companyIdTrimmed = pa?.companyId != null ? String(pa.companyId).trim() : ''
  const residentCompanyId = companyIdTrimmed !== '' ? companyIdTrimmed : String(id)
  const residentId = row.residentId ?? row.resident_id
  const isResident = residentId != null && String(residentId).length > 0
  const leadership = pa?.leadership as Record<string, unknown> | undefined
  const topName =
    (leadership?.topOfficialName as string | undefined) ??
    (leadership?.topOfficial as string | undefined) ??
    '-'
  const innovation = pa?.innovationProfile as Record<string, unknown> | undefined

  const titleTrimmed = pa?.administrationName?.trim() ?? ''
  const pitchTrimmed = pa?.administrationDescription?.trim() ?? ''
  const { state: resolvedState } = resolvePublicAdministrationCityState(pa ?? undefined, row)
  const stateTrimmed = resolvedState ?? ''

  return {
    id: String(id),
    residentCompanyId,
    ecosystemHidden: readEcosystemHiddenFromRow(row),
    residentId:
      residentId != null && String(residentId).trim() !== ''
        ? String(residentId).trim()
        : null,
    title: titleTrimmed !== '' ? titleTrimmed : '-',
    activity: administrationTypeToLabel(pa?.administrationType as number | undefined),
    pitch: pitchTrimmed !== '' ? pitchTrimmed : '-',
    dirigenteMaximo: topName,
    numeroServidores: pa?.numberEmployers != null ? String(pa.numberEmployers) : '-',
    innovationAreaPresence: boolToSimNao(leadership?.hasInnovationDepartment) ?? '-',
    areaInovacao: summarizeInnovationArea(innovation, leadership),
    state: stateTrimmed !== '' ? stateTrimmed : '-',
    isInMyEcosystem: isResident,
    badgeLabel: isResident ? 'Órgão residente' : '',
    logo: typeof row.profileImage === 'string' && row.profileImage !== '' ? row.profileImage : undefined,
    raw: row
  }
}

export type PublicAdministrationDetailState = {
  id: string
  title: string
  activity?: string
  pitch?: string
  logo?: string
  dirigenteMaximo?: string
  numeroServidores?: string
  areaInovacao?: string
  state?: string
  isInMyEcosystem?: boolean
  badgeLabel?: string
  publicAdministrationType?: string
  cnpj?: string
  cidade?: string
  portePopulacional?: string
  orcamentoAnual?: string
  representante?: string
  email?: string
  contato?: string
  cpf?: string
  cargo?: string
  linkedin?: string
  mandatoAte?: string
  possuiAreaFormalInovacao?: string
  areaFormalInovacao?: string
  anoCriacao?: string
  qtdePessoasEquipe?: string
  temContratoGestaoICT?: string
  realizouProcedimentosMarcoLegal?: string
  verticaisImpacto?: string
  objetivoEntrarPrograma?: string
  existeOrcamentoCTI?: string
  valorEstimadoCTI?: string
  focalPointsList?: PublicAdministrationFocalPointDisplay[]
  registrationDate?: string
}

export interface PublicAdministrationFocalPointDisplay {
  nome: string
  email: string
  contato: string
  cargo: string
  linkedin?: string
}

export const mapRowToPublicAdministrationDetailState = (
  card: PublicAdministrationListCardModel
): PublicAdministrationDetailState => {
  const row = card.raw
  const rowRoot = row as Record<string, unknown>
  const rowData = (rowRoot.data ?? rowRoot.Data) as Record<string, unknown> | undefined
  const pa = getPublicAdministrationInfoFromRow(row)
  const leadership = pa?.leadership as Record<string, unknown> | undefined
  const innovation = pa?.innovationProfile as Record<string, unknown> | undefined
  const focal =
    pa?.focalPoints ??
    (rowData?.focalPoints as unknown[] | undefined) ??
    (rowRoot.focalPoints as unknown[] | undefined)
  const focalArr = Array.isArray(focal) ? (focal as Record<string, unknown>[]) : []
  const firstFocal = focalArr.length > 0 ? focalArr[0] : null
  const contact = firstFocal?.contact as Record<string, unknown> | undefined
  const pop = pa?.populationSize
  const popLabel =
    typeof pop === 'number' && pop in POPULATION_LABELS ? POPULATION_LABELS[pop] : pop != null ? String(pop) : undefined

  const focalPointsList: PublicAdministrationFocalPointDisplay[] = focalArr.map(fp => {
    const c = fp.contact as Record<string, unknown> | undefined
    const mobile = c?.mobile != null ? String(c.mobile).trim() : ''
    const phone = c?.phone != null ? String(c.phone).trim() : ''
    const contatoRaw = mobile !== '' ? mobile : phone !== '' ? phone : '-'
    const contato = formatPhoneBr(contatoRaw) ?? '-'
    return {
      nome: typeof fp.name === 'string' && fp.name.trim() !== '' ? fp.name : '-',
      email: typeof fp.email === 'string' && fp.email.trim() !== '' ? fp.email : '-',
      contato,
      cargo: typeof fp.position === 'string' && fp.position.trim() !== '' ? fp.position : '-',
      linkedin: typeof fp.linkedin === 'string' && fp.linkedin.trim() !== '' ? fp.linkedin : undefined
    }
  })

  const { city: resolvedCity, state: resolvedStateDetail } = resolvePublicAdministrationCityState(pa ?? undefined, row)
  const cidade = resolvedCity

  const mandato =
    formatIsoDateBr(leadership?.termUntil) ??
    (typeof leadership?.termUntil === 'string' && leadership.termUntil.trim() !== ''
      ? leadership.termUntil
      : undefined) ??
    formatIsoDateBr(leadership?.mandateEnd) ??
    (typeof leadership?.mandateEnd === 'string' ? leadership.mandateEnd : undefined)

  const deptName =
    trimNonEmpty(leadership?.departmentName) ??
    trimNonEmpty((leadership as Record<string, unknown> | undefined)?.departamentName) ??
    trimNonEmpty((leadership as Record<string, unknown> | undefined)?.DepartamentName) ??
    trimNonEmpty((leadership as Record<string, unknown> | undefined)?.DepartmentName) ??
    trimNonEmpty(innovation?.departmentName) ??
    trimNonEmpty((innovation as Record<string, unknown> | undefined)?.departamentName) ??
    trimNonEmpty((innovation as Record<string, unknown> | undefined)?.DepartamentName) ??
    trimNonEmpty((innovation as Record<string, unknown> | undefined)?.DepartmentName) ??
    ''
  const areaFormal = deptName !== '' && deptName !== '-' ? deptName : undefined

  const hasManagementContractRaw =
    leadership?.hasManagementContract ??
    (leadership as Record<string, unknown> | undefined)?.HasManagementContract
  const hasManagementContract =
    typeof hasManagementContractRaw === 'boolean' ? hasManagementContractRaw : undefined

  const contratoDesc =
    trimNonEmpty(leadership?.managementContractDescription) ??
    trimNonEmpty((leadership as Record<string, unknown> | undefined)?.ManagementContractDescription) ??
    trimNonEmpty((leadership as Record<string, unknown> | undefined)?.managmentContractDesctipt) ??
    trimNonEmpty((leadership as Record<string, unknown> | undefined)?.ManagmentContractDesctipt) ??
    ''

  const temContratoGestaoICT =
    hasManagementContract === true
      ? contratoDesc !== ''
        ? `Sim, ${contratoDesc}`
        : 'Sim'
      : hasManagementContract === false
        ? 'Não'
        : undefined

  const cpfRaw = typeof firstFocal?.document === 'string' ? firstFocal.document.trim() : ''
  const cpf = cpfRaw !== '' ? cpfRaw : undefined
  const rootContact =
    (rowData?.contact as Record<string, unknown> | undefined) ??
    (rowData?.Contact as Record<string, unknown> | undefined) ??
    ((rowRoot.contact ?? rowRoot.Contact) as Record<string, unknown> | undefined)
  const representative =
    trimNonEmpty(rowData?.contactName) ??
    trimNonEmpty(rowData?.ContactName) ??
    trimNonEmpty(rowRoot.contactName) ??
    trimNonEmpty(rowRoot.ContactName)
  const representativeEmail =
    trimNonEmpty(rowData?.email) ??
    trimNonEmpty(rowData?.Email) ??
    trimNonEmpty(rowRoot.email) ??
    trimNonEmpty(rowRoot.Email) ??
    (typeof firstFocal?.email === 'string' && firstFocal.email.trim() !== '' ? firstFocal.email : undefined)
  const representativeContact =
    trimNonEmpty(rootContact?.mobile) ??
    trimNonEmpty(rootContact?.Mobile) ??
    trimNonEmpty(rootContact?.phone) ??
    trimNonEmpty(rootContact?.Phone) ??
    (contact?.mobile != null
      ? String(contact.mobile)
      : contact?.phone != null
        ? String(contact.phone)
        : undefined)
  const representativeCpf =
    trimNonEmpty(rowData?.document) ??
    trimNonEmpty(rowData?.Document) ??
    trimNonEmpty(rowRoot.document) ??
    trimNonEmpty(rowRoot.Document) ??
    cpf
  const representativePosition =
    trimNonEmpty(rowData?.position) ??
    trimNonEmpty(rowData?.Position) ??
    trimNonEmpty(rowRoot.position) ??
    trimNonEmpty(rowRoot.Position) ??
    (typeof firstFocal?.position === 'string' && firstFocal.position.trim() !== '' ? firstFocal.position : undefined)
  const representativeLinkedin =
    trimNonEmpty(rowData?.linkedin) ??
    trimNonEmpty(rowData?.Linkedin) ??
    trimNonEmpty(rowRoot.linkedin) ??
    trimNonEmpty(rowRoot.Linkedin) ??
    (typeof firstFocal?.linkedin === 'string' && firstFocal.linkedin.trim() !== '' ? firstFocal.linkedin : undefined)

  return {
    id: card.id,
    title: card.title,
    activity: card.activity,
    pitch: card.pitch,
    logo: card.logo,
    dirigenteMaximo: card.dirigenteMaximo,
    numeroServidores: card.numeroServidores,
    areaInovacao: card.areaInovacao,
    state: resolvedStateDetail ?? card.state,
    isInMyEcosystem: card.isInMyEcosystem,
    badgeLabel: card.badgeLabel,
    publicAdministrationType: card.activity,
    cnpj: formatCnpj(pa?.cnpj),
    cidade,
    portePopulacional: popLabel,
    orcamentoAnual: formatCurrencyBr(pa?.estimatedAnnualBudget),
    representante: representative,
    email: representativeEmail,
    contato: formatPhoneBr(representativeContact),
    cpf: formatCpf(representativeCpf),
    cargo: representativePosition,
    linkedin: representativeLinkedin,
    mandatoAte: mandato,
    possuiAreaFormalInovacao: boolToSimNao(leadership?.hasInnovationDepartment),
    areaFormalInovacao: areaFormal,
    anoCriacao:
      leadership?.creationYear != null && leadership.creationYear !== ''
        ? String(leadership.creationYear)
        : undefined,
    qtdePessoasEquipe:
      leadership?.teamSize != null && leadership.teamSize !== ''
        ? String(leadership.teamSize)
        : undefined,
    temContratoGestaoICT,
    realizouProcedimentosMarcoLegal: proceduresIndicesToLabel(innovation?.proceduresUsed),
    verticaisImpacto: priorityImpactIndicesToLabel(innovation?.priorityImpactAreas),
    objetivoEntrarPrograma: programGoalIndicesToLabel(innovation?.programGoals),
    existeOrcamentoCTI: boolToSimNao(innovation?.hasCTIBudget),
    valorEstimadoCTI: formatCurrencyBr(innovation?.annualCTIBudgetAmount),
    focalPointsList,
    registrationDate: formatIsoDateBr((pa as Record<string, unknown> | null)?.createdAt)
  }
}
